import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { ClipboardList, BarChart3 } from 'lucide-react';
import Layout from '@/components/Layout';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AmazonPrestationTracking = ({ user, activeModule = 'b2b' }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('assistance');
  const [records, setRecords] = useState([]);
  const [stats, setStats] = useState({ total: 0, total_points_delivered: 0, total_returns: 0, avg_points_delivered: 0, avg_returns: 0 });
  const [sites, setSites] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState(() => {
    const today = new Date();
    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
    return {
      startDate: monthStart,
      endDate: today.toISOString().split('T')[0],
      siteId: 'all',
      userId: 'all'
    };
  });

  const amazonSites = useMemo(
    () => sites.filter(site => !/colis|privé|prive/i.test(site.name || '')),
    [sites]
  );

  useEffect(() => {
    const fetchFiltersData = async () => {
      try {
        const [sitesRes, usersRes] = await Promise.all([
          axios.get(`${API}/sites`, { withCredentials: true }),
          axios.get(`${API}/users`, { withCredentials: true })
        ]);
        const allSites = sitesRes.data || [];
        const amazonSiteIds = new Set(allSites.filter(site => !/colis|privé|prive/i.test(site.name || '')).map(site => site.site_id));
        setSites(allSites);
        setEmployees((usersRes.data || []).filter(u => u.role === 'employee' && amazonSiteIds.has(u.site_id)));
      } catch (error) {
        console.error('Failed to load tracking filters data:', error);
      }
    };
    fetchFiltersData();
  }, []);

  useEffect(() => {
    const fetchTracking = async () => {
      setLoading(true);
      try {
        const params = {
          prestation_type: activeTab,
          start_date: filters.startDate,
          end_date: filters.endDate
        };
        if (filters.siteId !== 'all') params.site_id = filters.siteId;
        if (filters.userId !== 'all') params.user_id = filters.userId;

        const res = await axios.get(`${API}/prestations/amazon-tracking`, {
          params,
          withCredentials: true
        });
        setRecords(res.data.records || []);
        setStats(res.data.stats || { total: 0, total_points_delivered: 0, total_returns: 0, avg_points_delivered: 0, avg_returns: 0 });
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Erreur de chargement du suivi des prestations');
        setRecords([]);
      } finally {
        setLoading(false);
      }
    };
    fetchTracking();
  }, [activeTab, filters]);

  return (
    <Layout user={user} activeModule={activeModule}>
    <div className="space-y-6" data-testid="amazon-prestation-tracking-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#0F172A]">Suivi prestations Amazon</h1>
          <div className="mt-2 flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate('/admin/b2b')}
              data-testid="tracking-go-dashboard-button"
            >
              Retour dashboard
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate('/admin')}
              data-testid="tracking-go-home-button"
            >
              Retour accueil
            </Button>
          </div>
        </div>
        <div className="inline-flex rounded-lg border bg-white p-1">
          <Button
            size="sm"
            variant={activeTab === 'assistance' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('assistance')}
            data-testid="tracking-tab-assistance"
          >
            Assistance chef d'équipe
          </Button>
          <Button
            size="sm"
            variant={activeTab === 'backup' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('backup')}
            data-testid="tracking-tab-backup"
          >
            Backup
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="pt-6 grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="text-xs text-gray-500">Date début</label>
            <Input type="date" value={filters.startDate} onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))} data-testid="tracking-filter-start-date" />
          </div>
          <div>
            <label className="text-xs text-gray-500">Date fin</label>
            <Input type="date" value={filters.endDate} onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))} data-testid="tracking-filter-end-date" />
          </div>
          <div>
            <label className="text-xs text-gray-500">Site</label>
            <select className="w-full h-10 border rounded-md px-3" value={filters.siteId} onChange={(e) => setFilters(prev => ({ ...prev, siteId: e.target.value }))} data-testid="tracking-filter-site">
              <option value="all">Tous les sites Amazon</option>
              {amazonSites.map(site => <option key={site.site_id} value={site.site_id}>{site.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-500">Salarié</label>
            <select className="w-full h-10 border rounded-md px-3" value={filters.userId} onChange={(e) => setFilters(prev => ({ ...prev, userId: e.target.value }))} data-testid="tracking-filter-user">
              <option value="all">Tous les salariés</option>
              {employees.map(emp => <option key={emp.user_id} value={emp.user_id}>{emp.name}</option>)}
            </select>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm text-gray-500">Total prestations</CardTitle></CardHeader>
          <CardContent><p className="text-2xl font-bold" data-testid="tracking-total-records">{stats.total || 0}</p></CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm text-gray-500">Total points livrés</CardTitle></CardHeader>
          <CardContent><p className="text-2xl font-bold" data-testid="tracking-total-points">{stats.total_points_delivered || 0}</p></CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm text-gray-500">Total retours</CardTitle></CardHeader>
          <CardContent><p className="text-2xl font-bold" data-testid="tracking-total-returns">{stats.total_returns || 0}</p></CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {activeTab === 'assistance' ? <ClipboardList className="w-5 h-5" /> : <BarChart3 className="w-5 h-5" />}
            {activeTab === 'assistance' ? "Prestations Assistance chef d'équipe" : 'Prestations Backup'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-10 text-center text-gray-500" data-testid="tracking-loading">Chargement...</div>
          ) : records.length === 0 ? (
            <div className="py-10 text-center text-gray-500" data-testid="tracking-empty">Aucune prestation trouvée</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="tracking-table">
                <thead>
                  <tr className="border-b text-left text-gray-600">
                    <th className="p-2">Date</th>
                    <th className="p-2">Salarié</th>
                    <th className="p-2">Site</th>
                    <th className="p-2">Plaque</th>
                    <th className="p-2">Horaire</th>
                    {activeTab === 'assistance' ? <th className="p-2">Commentaire</th> : <><th className="p-2">Points livrés</th><th className="p-2">Retours</th></>}
                  </tr>
                </thead>
                <tbody>
                  {records.map((record) => (
                    <tr key={record.schedule_id} className="border-b">
                      <td className="p-2">{record.date}</td>
                      <td className="p-2">{record.user_name || record.user_id}</td>
                      <td className="p-2">{record.site_name || '-'}</td>
                      <td className="p-2">{record.vehicle_plate || '-'}</td>
                      <td className="p-2">{record.start_time || '-'} → {record.end_time || '-'}</td>
                      {activeTab === 'assistance' ? (
                        <td className="p-2">{record.assistance_comment || '-'}</td>
                      ) : (
                        <>
                          <td className="p-2">{record.backup_points_delivered ?? '-'}</td>
                          <td className="p-2">{record.backup_returns ?? '-'}</td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
    </Layout>
  );
};

export default AmazonPrestationTracking;